import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Account } from '@aveva/connect-web-core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { CONFIGURATION } from 'src/configuration';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private http = inject(HttpClient);
  private configuration = inject(CONFIGURATION);

  private accountsCache = new Map<string, Account>();
  private cacheTimestamp: number | null = null;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  queryAccounts(accountIds: string): Observable<Account[]> {
    if (this.isCacheValid()) {
      const ids = accountIds.split(',');
      const cachedAccounts = ids.map(id => this.accountsCache.get(id)).filter(Boolean) as Account[];
      if (cachedAccounts.length === ids.length) {
        return of(cachedAccounts);
      }
    }

    const params = new HttpParams().set('ids', accountIds);
    return this.http.get<Account[]>(`${this.configuration.apis.am}/accounts/`, { params: params })
      .pipe(
        map((data: any) => data._embedded['doc:accounts']), //eslint-disable-line  @typescript-eslint/no-explicit-any
        tap(accounts => {
          this.cacheTimestamp = Date.now();
          accounts.forEach(account => this.accountsCache.set(account.id, account));
        }),
        shareReplay(1),
        catchError(this.handleError)
      );
  }

  getAccount(accountId: string): Observable<Account> {
    if (this.isCacheValid() && this.accountsCache.has(accountId)) {
      return of(this.accountsCache.get(accountId)!);
    }

    return this.http.get<Account>(`${this.configuration.apis.am}/accounts/${accountId}`)
      .pipe(
        tap(account => {
          this.cacheTimestamp = Date.now();
          this.accountsCache.set(account.id, account);
        }),
        catchError(this.handleError)
      );
  }

  private isCacheValid(): boolean {
    return this.cacheTimestamp !== null && (Date.now() - this.cacheTimestamp) < this.CACHE_TTL;
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      return throwError(() => new Error(`Error: ${error.message}`));
    } else {
      // Server-side errors
      return throwError(() => new Error(`Error: Backend returned code ${error.status}: ${error.message}`));
    }
  }
}
